import React, { useState, useMemo, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { Card, Table, Modal, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { withTranslation } from 'react-i18next';
import { BiSort, BiSortUp, BiSortDown, BiInfoCircle, BiChevronRight, BiChevronDown } from 'react-icons/bi';
import { BsFileEarmarkExcel } from 'react-icons/bs';
import * as XLSX from 'xlsx';
import Utils from '../../../core/utilities/Utils';
import { isConsolidatedPerformanceExplicitNull } from '../../../core/utilities/esgConsolidatedPerformance';
import CompanyDashboardService from '../../../core/service/CompanyDashboardService';
import EquationComponentsPopover from '../../../components/EquationComponentsPopover/EquationComponentsPopover';
import ESGAnalyticsDPBreakdownModal from './ESGAnalyticsDPBreakdownModal';
import {
  getBranchScopedFilter,
  findConfigRowForLevel1Item,
  buildNullDatapointBreakdownPayload,
} from './esgAnalyticsNullBreakdown';
import './ESGAnalyticsCards.scss';

const monthOrder = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const quarterOrder = ['Q1', 'Q2', 'Q3', 'Q4'];
const EMISSION_DISPLAY_UOM = 'tCO2-e';

function collectDatapointsFromConfigRow(configRow) {
  if (!configRow) return [];
  const seen = new Map();
  const add = (id, code, name) => {
    const dedupeKey = id != null ? `id:${id}` : `cn:${String(code || '')}|${String(name || '')}`;
    if (seen.has(dedupeKey)) return;
    const c = code != null ? String(code).trim() : '';
    const n = name != null ? String(name).trim() : '';
    seen.set(dedupeKey, { id, code: c, name: n });
  };

  if (Array.isArray(configRow.level2Rows)) {
    configRow.level2Rows.forEach((row) => {
      if (row.datapoint) {
        const dp = row.datapoint;
        add(
          dp.id,
          dp.code || dp.dataPointCode,
          dp.name || dp.dataPointName || row.label
        );
      }
    });
  }

  if (Array.isArray(configRow.level2)) {
    configRow.level2.forEach((item) => {
      if (item.itemType === 'datapoint' && item.id != null) {
        add(
          item.id,
          item.code || item.dataPointCode,
          item.name || item.dataPointName || item.label
        );
      }
    });
  }

  return Array.from(seen.values());
}

function formatNullModalDatapointLine(code, name) {
  if (code && name) return `${code} — ${name}`;
  if (code) return code;
  if (name) return name;
  return '';
}

function getPeriodBucketFromDataView(dataViewData, periodType, period) {
  if (!dataViewData || typeof dataViewData !== 'object') return null;
  if (periodType === 'YEARLY') {
    const yearData = dataViewData[period];
    if (!yearData || typeof yearData !== 'object') return null;
    return yearData.YEARLY || Object.values(yearData)[0];
  }
  const [year, periodKey] = String(period).split('-');
  const yearData = dataViewData[year];
  if (!yearData || typeof yearData !== 'object') return null;
  return yearData[periodKey];
}

/** Lines shown in the null-values modal: prefer L2 datapoints when L1 is a metric. */
function getNullModalDisplayLines(configRows, level1Item) {
  if (!level1Item) {
    return [];
  }

  if (level1Item.itemType === 'datapoint') {
    const code = level1Item.code != null ? String(level1Item.code).trim() : '';
    const name = level1Item.name != null ? String(level1Item.name).trim() : '';
    const text = formatNullModalDatapointLine(code, name) || String(level1Item.label || '').trim();
    if (!text) return [];
    return [{ text, sortLabel: `${code}|${name}` }];
  }

  const configRow = findConfigRowForLevel1Item(configRows, level1Item);
  const datapoints = collectDatapointsFromConfigRow(configRow);

  if (datapoints.length > 0) {
    return datapoints
      .map((dp) => {
        const text = formatNullModalDatapointLine(dp.code, dp.name);
        return {
          text,
          sortLabel: `${dp.code}|${dp.name}`
        };
      })
      .filter((entry) => entry.text);
  }

  const code = level1Item.code != null ? String(level1Item.code).trim() : '';
  const name = level1Item.name != null ? String(level1Item.name).trim() : '';
  const text = formatNullModalDatapointLine(code, name) || String(level1Item.label || '').trim();
  if (!text) return [];
  return [{ text, sortLabel: `${code}|${name}` }];
}

const ESGAnalyticsCards = ({ t, configRows, dataViewData, filter, branches, dataPoints, yearData, section = 'all', equationPopoverClassName }) => {
  // Sort state for entity level summary
  const [sortColumn, setSortColumn] = useState(null); // null, 'entity', or 'year-itemId'
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'

  // Modal state for level 2 breakdown
  const [showBreakdownModal, setShowBreakdownModal] = useState(false);
  const [breakdownData, setBreakdownData] = useState(null); // {entity, level1Item}
  const [expandedLevel2Rows, setExpandedLevel2Rows] = useState({});
  const [level2DrilldownData, setLevel2DrilldownData] = useState({});
  // Nested drilldown state: {parentRowKey-nestedRowKey: {loading, error, rows}}
  const [nestedDrilldownData, setNestedDrilldownData] = useState({});
  const [expandedNestedRows, setExpandedNestedRows] = useState({});
  const [showNullValuesModal, setShowNullValuesModal] = useState(false);
  /** { title, period, periodType, branchId, requestPayload } | null */
  const [nullDpModalContext, setNullDpModalContext] = useState(null);

  const filterSignature = useMemo(() => JSON.stringify(filter ?? null), [filter]);

  useEffect(() => {
    setExpandedLevel2Rows({});
    setLevel2DrilldownData({});
    setNestedDrilldownData({});
    setExpandedNestedRows({});
    setNullDpModalContext(null);
  }, [filterSignature]);

  // Extract periods from dataViewData or use yearData if available
  // For YEARLY: returns ["2024", "2025"] (chronological)
  // For QUARTERLY: returns ["2024-Q1", "2024-Q2", "2025-Q1", ...] (chronological)
  // For MONTHLY: returns ["2024-JAN", "2024-FEB", "2025-JAN", ...] (chronological)
  const getPeriods = () => {
    const periodType = filter?.periodType || 'YEARLY';

    const sortPeriodsChronologically = (periods) => {
      if (!periods || periods.length === 0) return periods;
      return [...periods].sort((a, b) => {
        if (periodType === 'YEARLY') {
          return parseInt(a, 10) - parseInt(b, 10);
        }
        const [yA, pA] = a.includes('-') ? a.split('-') : [a, ''];
        const [yB, pB] = b.includes('-') ? b.split('-') : [b, ''];
        if (yA !== yB) return parseInt(yA, 10) - parseInt(yB, 10);
        if (periodType === 'MONTHLY' && monthOrder.includes(pA) && monthOrder.includes(pB)) {
          return monthOrder.indexOf(pA) - monthOrder.indexOf(pB);
        }
        if (periodType === 'QUARTERLY' && quarterOrder.includes(pA) && quarterOrder.includes(pB)) {
          return quarterOrder.indexOf(pA) - quarterOrder.indexOf(pB);
        }
        return String(pA).localeCompare(String(pB));
      });
    };

    if (yearData && Array.isArray(yearData) && yearData.length > 0) {
      return sortPeriodsChronologically(yearData);
    }
    if (dataViewData && typeof dataViewData === 'object') {
      const periods = [];
      const years = Object.keys(dataViewData).filter(y => /^\d{4}$/.test(y)).sort((a, b) => parseInt(a, 10) - parseInt(b, 10));

      years.forEach(year => {
        const yd = dataViewData[year];
        if (!yd || typeof yd !== 'object') return;
        if (periodType === 'YEARLY') {
          periods.push(year);
        } else {
          let periodKeys = Object.keys(yd).filter(p => p !== 'YEARLY');
          if (periodType === 'MONTHLY') {
            periodKeys = periodKeys.filter(p => monthOrder.includes(p)).sort((a, b) => monthOrder.indexOf(a) - monthOrder.indexOf(b));
          } else if (periodType === 'QUARTERLY') {
            periodKeys = periodKeys.filter(p => quarterOrder.includes(p)).sort((a, b) => quarterOrder.indexOf(a) - quarterOrder.indexOf(b));
          } else {
            periodKeys = periodKeys.sort((a, b) => a.localeCompare(b));
          }
          periodKeys.forEach(period => {
            periods.push(`${year}-${period}`);
          });
        }
      });
      return periods;
    }
    return [];
  };

  const periods = getPeriods();
  const periodType = filter?.periodType || 'YEARLY';

  // Helper to format period label for display
  const getPeriodLabel = (period) => {
    if (periodType === 'YEARLY') {
      return period;
    } else {
      // For quarterly/monthly, format as "2025 Q1" or "2025 JAN"
      const [year, periodKey] = period.split('-');
      return `${year} ${periodKey}`;
    }
  };

  const isDrillableLevel2Row = (level2Item) => {
    if (!level2Item?.drillDownEnabled) return false;
    // Show expand icon for equation-based drilldown (metric or equation rows) when eqExpressionWithId is present
    if (level2Item.drillDownType === 'equation') {
      return (level2Item.itemType === 'equation' || level2Item.itemType === 'metric') && !!level2Item.eqExpressionWithId;
    }
    // Show expand icon for metric-based drilldown on metric rows when we have an id
    if (level2Item.drillDownType === 'metric') {
      return level2Item.itemType === 'metric' && !!level2Item.id;
    }
    return false;
  };

  const getDrilldownRowsFromResponse = (drilldownResponse, scopedBranchId = null) => {
    if (!drilldownResponse || typeof drilldownResponse !== 'object') return [];
    const drilldownRowsMap = new Map();

    periods.forEach((period) => {
      let periodData = null;
      if (periodType === 'YEARLY') {
        const yearly = drilldownResponse[period];
        periodData = yearly ? (yearly.YEARLY || Object.values(yearly)[0]) : null;
      } else {
        const [year, periodKey] = period.split('-');
        periodData = drilldownResponse?.[year]?.[periodKey];
      }
      if (!periodData || typeof periodData !== 'object') return;

      const upsertRow = (itemKey, rowData) => {
        const existing = drilldownRowsMap.get(itemKey) || {
          key: itemKey,
          name: rowData.name,
          itemType: rowData.itemType,
          code: rowData.code,
          valuesByPeriod: {},
          uomByPeriod: {},
          reportedValueByPeriod: {},
          reportedUomByPeriod: {},
          eqExpression: rowData.eqExpression || null,
          eqExpressionWithId: rowData.eqExpressionWithId || null,
          branchId: scopedBranchId || null
        };
        existing.valuesByPeriod[period] = rowData.total;
        existing.uomByPeriod[period] = rowData.uom || '';
        if (rowData.reportedValue !== undefined || rowData.reportedUom !== undefined) {
          if (!existing.reportedValueByPeriod) existing.reportedValueByPeriod = {};
          if (!existing.reportedUomByPeriod) existing.reportedUomByPeriod = {};
          if (rowData.reportedValue !== undefined) {
            existing.reportedValueByPeriod[period] = rowData.reportedValue;
          }
          if (rowData.reportedUom !== undefined) {
            existing.reportedUomByPeriod[period] = rowData.reportedUom || '';
          }
        }
        // Preserve eqExpression and eqExpressionWithId if they exist
        if (rowData.eqExpression && !existing.eqExpression) {
          existing.eqExpression = rowData.eqExpression;
        }
        if (rowData.eqExpressionWithId && !existing.eqExpressionWithId) {
          existing.eqExpressionWithId = rowData.eqExpressionWithId;
        }
        drilldownRowsMap.set(itemKey, existing);
      };

      const pickScopedValue = (entry) => {
        if (!scopedBranchId) {
          return entry?.total;
        }
        const branchData = entry?.branchConsolidated?.find((b) => b.branchId === scopedBranchId);
        return branchData?.value ?? null;
      };

      const pickScopedDatapointReported = (dp) => {
        if (!scopedBranchId) {
          return {
            value: dp?.valueBeforeConversion ?? null,
            uom: dp?.uomBeforeConversion || ''
          };
        }
        const branchData = dp?.branchConsolidated?.find((b) => b.branchId === scopedBranchId);
        return {
          value: branchData?.valueBeforeConversion ?? dp?.valueBeforeConversion ?? null,
          uom: branchData?.uomBeforeConversion || dp?.uomBeforeConversion || ''
        };
      };

      (periodData.metrics || []).forEach((metric) => {
        if (metric?.metricCode) {
          const scopedValue = pickScopedValue(metric);
          upsertRow(`metric-${metric.metricCode}`, {
            name: metric.metricName || metric.metricCode,
            itemType: 'metric',
            code: metric.metricCode,
            total: scopedValue,
            uom: metric.uom,
            eqExpression: metric.eqExpression || null,
            eqExpressionWithId: metric.eqExpressionWithId || null
          });
        }
      });
      (periodData.datapoints || []).forEach((dp) => {
        if (dp?.dataPointCode) {
          const scopedValue = pickScopedValue(dp);
          const reported = pickScopedDatapointReported(dp);
          upsertRow(`datapoint-${dp.dataPointCode}`, {
            name: dp.dataPointName || dp.dataPointCode,
            itemType: 'datapoint',
            code: dp.dataPointCode,
            total: scopedValue,
            uom: getDatapointDisplayUom(dp, dp.uom),
            reportedValue: reported.value,
            reportedUom: reported.uom
          });
        }
      });
    });

    return Array.from(drilldownRowsMap.values());
  };

  const handleLevel2RowToggle = async (parentIndex, level2Item, scopedBranchId = null) => {
    const rowKey = `${parentIndex}-${level2Item.id || level2Item.eqExpressionWithId || level2Item.code || level2Item.name}`;
    const isExpanded = expandedLevel2Rows[rowKey] === true;

    if (isExpanded) {
      setExpandedLevel2Rows((prev) => ({ ...prev, [rowKey]: false }));
      return;
    }

    setExpandedLevel2Rows((prev) => ({ ...prev, [rowKey]: true }));

    const isMetricDrill = level2Item.drillDownType === 'metric';
    let metricCodes = [];
    let datapointCodes = [];
    if (!isMetricDrill) {
      const equation = level2Item.eqExpressionWithId || '';
      const _ = Utils.extractCodesForEquationDrilldown(equation);
      metricCodes = _.metricCodes;
      datapointCodes = _.datapointCodes;
      if (metricCodes.length === 0 && datapointCodes.length === 0) {
        setLevel2DrilldownData((prev) => ({
          ...prev,
          [rowKey]: {
            loading: false,
            error: t("ESGAnalytics.noDataAvailable.label"),
            rows: []
          }
        }));
        return;
      }
    }

    const payload = {
      ...getBranchScopedFilter(filter, scopedBranchId),
      equations: [],
      metricIds: [],
      datapointIds: [],
      equationConfig: [],
      metricCodes,
      datapointCodes,
      ...(isMetricDrill
        ? { expandableMetricIds: [level2Item.id].filter(Boolean) }
        : {})
    };

    setLevel2DrilldownData((prev) => ({
      ...prev,
      [rowKey]: { loading: true, error: '', rows: [] }
    }));

    try {
      const res = await CompanyDashboardService.getMetricDVAnalytics(payload);
      if (res?.status !== 200) {
        throw new Error('Failed to fetch drilldown');
      }
      const rows = getDrilldownRowsFromResponse(res.data, scopedBranchId);
      setLevel2DrilldownData((prev) => ({
        ...prev,
        [rowKey]: { loading: false, error: '', rows }
      }));
    } catch (error) {
      setLevel2DrilldownData((prev) => ({
        ...prev,
        [rowKey]: {
          loading: false,
          error: t("ESGAnalytics.noDataAvailable.label"),
          rows: []
        }
      }));
    }
  };

  const isNestedDrillableRow = (drillRow) => {
    return drillRow?.itemType === 'metric' && !!drillRow?.eqExpressionWithId;
  };

  const handleNestedRowToggle = async (parentRowKey, drillRow) => {
    const nestedRowKey = `${parentRowKey}-${drillRow.key || drillRow.code || drillRow.name}`;
    const isExpanded = expandedNestedRows[nestedRowKey] === true;
    const scopedBranchId = drillRow?.branchId || null;

    if (isExpanded) {
      setExpandedNestedRows((prev) => ({ ...prev, [nestedRowKey]: false }));
      return;
    }

    setExpandedNestedRows((prev) => ({ ...prev, [nestedRowKey]: true }));

    const equationWithId = drillRow.eqExpressionWithId || '';
    const { metricCodes, datapointCodes } = Utils.extractCodesForEquationDrilldown(equationWithId);

    if (metricCodes.length === 0 && datapointCodes.length === 0) {
      setNestedDrilldownData((prev) => ({
        ...prev,
        [nestedRowKey]: {
          loading: false,
          error: t("ESGAnalytics.noDataAvailable.label"),
          rows: []
        }
      }));
      return;
    }

    const payload = {
      ...getBranchScopedFilter(filter, scopedBranchId),
      equations: [],
      metricIds: [],
      datapointIds: [],
      equationConfig: [],
      metricCodes,
      datapointCodes
    };

    setNestedDrilldownData((prev) => ({
      ...prev,
      [nestedRowKey]: { loading: true, error: '', rows: [] }
    }));

    try {
      const res = await CompanyDashboardService.getMetricDVAnalytics(payload);
      if (res?.status !== 200) {
        throw new Error('Failed to fetch nested drilldown');
      }
      const rows = getDrilldownRowsFromResponse(res.data, scopedBranchId);
      setNestedDrilldownData((prev) => ({
        ...prev,
        [nestedRowKey]: { loading: false, error: '', rows }
      }));
    } catch (error) {
      setNestedDrilldownData((prev) => ({
        ...prev,
        [nestedRowKey]: {
          loading: false,
          error: t("ESGAnalytics.noDataAvailable.label"),
          rows: []
        }
      }));
    }
  };

  const renderDrilldownLoadingState = (rowKey, columnPeriods = periods, showReportedValueColumn = false) => (
    <tr className="child-row">
      <td className="ps-5 text-muted">{t("Loading.label") || 'Loading...'}</td>
      {columnPeriods.map((p) => (
        <React.Fragment key={`${rowKey}-${p}-loading`}>
          <td className="text-end">-</td>
          {showReportedValueColumn && <td className="text-end">-</td>}
        </React.Fragment>
      ))}
    </tr>
  );

  const renderDrilldownErrorState = (rowKey, error, columnPeriods = periods, showReportedValueColumn = false) => (
    <tr className="child-row">
      <td className="ps-5 text-muted">{error}</td>
      {columnPeriods.map((p) => (
        <React.Fragment key={`${rowKey}-${p}-error`}>
          <td className="text-end">-</td>
          {showReportedValueColumn && <td className="text-end">-</td>}
        </React.Fragment>
      ))}
    </tr>
  );

  const renderDrilldownEmptyState = (rowKey, columnPeriods = periods, showReportedValueColumn = false) => (
    <tr className="child-row">
      <td className="ps-5 text-muted">{t("ESGAnalytics.noDataAvailable.label")}</td>
      {columnPeriods.map((p) => (
        <React.Fragment key={`${rowKey}-${p}-empty`}>
          <td className="text-end">-</td>
          {showReportedValueColumn && <td className="text-end">-</td>}
        </React.Fragment>
      ))}
    </tr>
  );

  const renderRecursiveDrillRows = (
    parentKey,
    rows,
    depth = 1,
    columnPeriods = periods,
    showReportedValueColumn = false
  ) => {
    if (!rows || rows.length === 0) return null;

    const depthPaddingClass = depth <= 1 ? 'ps-5' : `ps-${Math.min(depth + 4, 5)}`;

    return rows.map((drillRow, drillIdx) => {
      const currentRowKey = `${parentKey}-${drillRow.key || drillRow.code || drillRow.name}`;
      const currentState = nestedDrilldownData[currentRowKey];
      const isExpanded = expandedNestedRows[currentRowKey] === true;
      const canDrill = isNestedDrillableRow(drillRow);
      const eqIdForComponents = drillRow?.itemType === 'metric' ? (drillRow?.eqExpressionWithId || null) : null;

      return (
        <React.Fragment key={`${currentRowKey}-${drillIdx}`}>
          <tr
            className={`child-row ${canDrill ? 'cursor' : ''}`}
            onClick={() => {
              if (canDrill) {
                handleNestedRowToggle(parentKey, drillRow);
              }
            }}
          >
            <td className={depthPaddingClass}>
              {depth > 1 && <span className={`ms-${Math.min(depth * 2, 5)}`} />}
              <span className="text-muted">└─</span>
              {canDrill ? (
                <span className="ms-1 me-1 d-inline-flex align-items-center">
                  {isExpanded ? <BiChevronDown size={16} /> : <BiChevronRight size={16} />}
                </span>
              ) : (
                <span className="ms-1 me-1 d-inline-flex align-items-center" style={{ width: '16px' }} />
              )}
              {drillRow.name}
              <EquationComponentsPopover
                standardId={filter?.standardId}
                equationId={eqIdForComponents}
                headerText={drillRow.eqExpression || drillRow.eqExpressionWithId || drillRow.code || drillRow.name}
                popoverKey={currentRowKey}
                className="esg-eq-info-trigger ms-2"
                popoverClassName={equationPopoverClassName}
                iconSize={14}
                stopPropagation={true}
                showWhenNoEquationId={false}
              />
            </td>
            {columnPeriods.map((p) => {
              const rawValue = drillRow.valuesByPeriod?.[p];
              const value = rawValue != null ? Utils.formattedNumber(rawValue) : '-';
              const uom = drillRow.uomByPeriod?.[p] || '';
              const rawReported = drillRow.reportedValueByPeriod?.[p];
              const reportedVal = rawReported != null ? Utils.formattedNumber(rawReported) : '-';
              const reportedUom = drillRow.reportedUomByPeriod?.[p] || '';
              return (
                <React.Fragment key={`${currentRowKey}-${p}`}>
                  <td className="text-end">
                    {value} {uom && value !== '-' && <span className="text-muted">({uom})</span>}
                  </td>
                  {showReportedValueColumn && (
                    <td className="text-end">
                      {reportedVal}
                      {reportedUom && reportedVal !== '-' && (
                        <span className="text-muted ms-1">({reportedUom})</span>
                      )}
                    </td>
                  )}
                </React.Fragment>
              );
            })}
          </tr>

          {isExpanded && canDrill && currentState?.loading && (
            <tr className="child-row">
              <td className={`${depthPaddingClass} text-muted`}>
                {depth > 1 && <span className={`ms-${Math.min(depth * 2, 5)}`} />}
                {t("Loading.label") || 'Loading...'}
              </td>
              {columnPeriods.map((p) => (
                <React.Fragment key={`${currentRowKey}-${p}-loading`}>
                  <td className="text-end">-</td>
                  {showReportedValueColumn && <td className="text-end">-</td>}
                </React.Fragment>
              ))}
            </tr>
          )}

          {isExpanded && canDrill && !currentState?.loading && currentState?.error && (
            <tr className="child-row">
              <td className={`${depthPaddingClass} text-muted`}>
                {depth > 1 && <span className={`ms-${Math.min(depth * 2, 5)}`} />}
                {currentState.error}
              </td>
              {columnPeriods.map((p) => (
                <React.Fragment key={`${currentRowKey}-${p}-error`}>
                  <td className="text-end">-</td>
                  {showReportedValueColumn && <td className="text-end">-</td>}
                </React.Fragment>
              ))}
            </tr>
          )}

          {isExpanded && canDrill && !currentState?.loading && !currentState?.error && (!currentState?.rows || currentState.rows.length === 0) && (
            <tr className="child-row">
              <td className={`${depthPaddingClass} text-muted`}>
                {depth > 1 && <span className={`ms-${Math.min(depth * 2, 5)}`} />}
                {t("ESGAnalytics.noDataAvailable.label")}
              </td>
              {columnPeriods.map((p) => (
                <React.Fragment key={`${currentRowKey}-${p}-empty`}>
                  <td className="text-end">-</td>
                  {showReportedValueColumn && <td className="text-end">-</td>}
                </React.Fragment>
              ))}
            </tr>
          )}

          {isExpanded && canDrill && !currentState?.loading && !currentState?.error && currentState?.rows?.length > 0 &&
            renderRecursiveDrillRows(currentRowKey, currentState.rows, depth + 1, columnPeriods, showReportedValueColumn)}
        </React.Fragment>
      );
    });
  };

  const renderDrilldownRows = (
    rowKey,
    level2Item,
    columnPeriods = periods,
    showReportedValueColumn = false
  ) => {
    const drillState = level2DrilldownData[rowKey];
    const cols = columnPeriods;

    if (drillState?.loading) {
      return renderDrilldownLoadingState(rowKey, cols, showReportedValueColumn);
    }

    if (drillState?.error) {
      return renderDrilldownErrorState(rowKey, drillState.error, cols, showReportedValueColumn);
    }

    if (!drillState?.rows || drillState.rows.length === 0) {
      return renderDrilldownEmptyState(rowKey, cols, showReportedValueColumn);
    }

    return renderRecursiveDrillRows(rowKey, drillState.rows, 1, cols, showReportedValueColumn);
  };

  const selectedEntities = useMemo(() => {
    if (!branches || branches.length === 0) return [];
    const selectedIds = new Set([...(filter?.parentEntities || []), ...(filter?.childEntities || [])]);
    if (selectedIds.size === 0) return [];
    return branches.filter((branch) =>
      branch?.branchId &&
      branch.branchName !== t("Total.label") &&
      selectedIds.has(branch.branchId)
    );
  }, [branches, filterSignature, t]);

  const level1Items = useMemo(() => {
    if (!Array.isArray(configRows)) return [];
    return configRows
      .map((configRow) => {
        const level1 = configRow?.level1;
        if (!level1) return null;

        const rowShowPercentage = configRow.showPercentage !== false;

        if (level1.itemType === 'equation' && level1.eqExpressionWithId) {
          return {
            id: level1.eqExpressionWithId,
            itemType: 'equation',
            label: configRow.name || level1.name || 'Equation',
            name: level1.name || configRow.name || 'Equation',
            code: level1.eqExpressionWithId,
            isEquation: true,
            equationId: level1.eqExpressionWithId,
            showPercentage: rowShowPercentage
          };
        }

        if (level1.id == null) return null;
        return {
          id: level1.id,
          itemType: level1.itemType || 'metric',
          label: configRow.name || level1.label || level1.name || '',
          name: level1.name || level1.metricName || level1.dataPointName || '',
          code: level1.code || level1.metricCode || level1.dataPointCode || '',
          isEquation: false,
          equationId: null,
          showPercentage: rowShowPercentage
        };
      })
      .filter(Boolean);
  }, [configRows]);

  /** One cell object for consolidated performance grid: branch value + optional partial-null hint from API. */
  const getConsPerfCellForLevel1 = (periodData, level1Item, branchId) => {
    if (!periodData || !level1Item || !branchId) {
      return { value: null, hasNullDps: false };
    }

    if (level1Item.isEquation && level1Item.equationId) {
      const equation = periodData.equations?.find((eq) => eq.equation === level1Item.equationId);
      const branchData = equation?.branchConsolidated?.find((b) => b.branchId === branchId);
      return {
        value: branchData?.value ?? null,
        hasNullDps: branchData?.hasNullDps === true
      };
    }

    if (level1Item.itemType === 'metric') {
      const metric = periodData.metrics?.find((m) => m.metricId == level1Item.id);
      const branchData = metric?.branchConsolidated?.find((b) => b.branchId === branchId);
      return {
        value: branchData?.value ?? null,
        hasNullDps: branchData?.hasNullDps === true
      };
    }

    if (level1Item.itemType === 'datapoint') {
      const datapoint = periodData.datapoints?.find((dp) => dp.dataPointId == level1Item.id);
      const branchData = datapoint?.branchConsolidated?.find((b) => b.branchId === branchId);
      return {
        value: branchData?.value ?? null,
        hasNullDps: branchData?.hasNullDps === true
      };
    }

    return { value: null, hasNullDps: false };
  };

  const getTotalForLevel1 = (periodData, level1Item) => {
    if (!periodData || !level1Item) return 0;

    if (level1Item.isEquation && level1Item.equationId) {
      const equation = periodData.equations?.find((eq) => eq.equation === level1Item.equationId);
      return equation?.total != null ? (parseFloat(equation.total) || 0) : 0;
    }

    if (level1Item.itemType === 'metric') {
      const metric = periodData.metrics?.find((m) => m.metricId == level1Item.id);
      return metric?.total != null ? (parseFloat(metric.total) || 0) : 0;
    }

    if (level1Item.itemType === 'datapoint') {
      const datapoint = periodData.datapoints?.find((dp) => dp.dataPointId == level1Item.id);
      return datapoint?.total != null ? (parseFloat(datapoint.total) || 0) : 0;
    }

    return 0;
  };

  const entityLevelData = useMemo(() => {
    if (!selectedEntities.length || !level1Items.length || !periods.length || !dataViewData) {
      return { entities: selectedEntities, level1Items, dataByEntityAndPeriod: {} };
    }

    const dataByEntityAndPeriod = {};
    selectedEntities.forEach((entity) => {
      dataByEntityAndPeriod[entity.branchId] = {};
    });

    periods.forEach((period, periodIndex) => {
      const periodData = getPeriodBucketFromDataView(dataViewData, periodType, period);
      if (!periodData) return;

      selectedEntities.forEach((entity) => {
        if (!dataByEntityAndPeriod[entity.branchId][periodIndex]) {
          dataByEntityAndPeriod[entity.branchId][periodIndex] = {};
        }

        level1Items.forEach((item) => {
          dataByEntityAndPeriod[entity.branchId][periodIndex][item.id] = getConsPerfCellForLevel1(
            periodData,
            item,
            entity.branchId
          );
        });
      });
    });

    return { entities: selectedEntities, level1Items, dataByEntityAndPeriod };
  }, [selectedEntities, level1Items, periods, dataViewData, periodType]);

  const hasEntityData = entityLevelData.entities.length > 0 && entityLevelData.level1Items.length > 0;

  const totalsByPeriod = useMemo(() => {
    const totals = {};
    periods.forEach((period, periodIndex) => {
      const periodData = getPeriodBucketFromDataView(dataViewData, periodType, period);
      totals[periodIndex] = {};
      level1Items.forEach((item) => {
        totals[periodIndex][item.id] = getTotalForLevel1(periodData, item);
      });
    });
    return totals;
  }, [periods, dataViewData, periodType, level1Items]);

  // Handle column sorting
  const handleSort = (columnKey) => {
    if (sortColumn === columnKey) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };

  // Get sort icon for column
  const getSortIcon = (columnKey) => {
    if (sortColumn !== columnKey) {
      return <BiSort className="ms-1" style={{ opacity: 0.3 }} />;
    }
    return sortDirection === 'asc'
      ? <BiSortUp className="ms-1" />
      : <BiSortDown className="ms-1" />;
  };

  // Sort entities based on selected column
  const getSortedEntities = () => {
    if (!sortColumn || !hasEntityData) {
      return entityLevelData.entities;
    }

    const sorted = [...entityLevelData.entities].sort((a, b) => {
      let aValue, bValue;

      if (sortColumn === 'entity') {
        // Alphabetical sort for entity name
        aValue = (a.branchName || '').toLowerCase();
        bValue = (b.branchName || '').toLowerCase();

        if (sortDirection === 'asc') {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      } else {
        // Numerical sort for data columns
        // Parse column key: "yearIndex-itemId-value" or "yearIndex-itemId-percent"
        // Note: itemId can be a string (for equations) or number (for metrics/datapoints)
        const parts = sortColumn.split('-');
        const yearIndex = parseInt(parts[0]);
        const columnType = parts[parts.length - 1]; // 'value' or 'percent' (last part)
        // Reconstruct itemId - everything between yearIndex and columnType
        // Handle case where itemId might contain dashes (for equation IDs)
        const itemIdParts = parts.slice(1, -1);
        const itemIdStr = itemIdParts.join('-');
        const itemId = itemIdStr;

        // Get base values for comparison
        // Note: JavaScript will handle string-to-number conversion for numeric keys if needed
        const aData = entityLevelData.dataByEntityAndPeriod[a.branchId]?.[yearIndex]?.[itemId]?.value;
        const bData = entityLevelData.dataByEntityAndPeriod[b.branchId]?.[yearIndex]?.[itemId]?.value;
        const total = totalsByPeriod[yearIndex]?.[itemId] || 0;

        // Handle null/undefined values - put at end
        const aHasValue = aData != null && !isNaN(parseFloat(aData));
        const bHasValue = bData != null && !isNaN(parseFloat(bData));

        if (!aHasValue && !bHasValue) return 0;
        if (!aHasValue) return 1; // Put null values at end
        if (!bHasValue) return -1; // Put null values at end

        if (columnType === 'percent') {
          // Sort by percentage
          const aPercent = total > 0 ? (parseFloat(aData) / total) * 100 : 0;
          const bPercent = total > 0 ? (parseFloat(bData) / total) * 100 : 0;
          aValue = aPercent;
          bValue = bPercent;
        } else {
          // Sort by value (default)
          aValue = parseFloat(aData);
          bValue = parseFloat(bData);
        }

        if (sortDirection === 'asc') {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      }
    });

    return sorted;
  };

  const sortedEntities = useMemo(() => getSortedEntities(), [sortColumn, sortDirection, entityLevelData, totalsByPeriod]);
  const nullValueHintText = t('ESGAnalytics.consolidatedNullValueHint.label') || '';

  const consPerfPeriodGroups = useMemo(() => (
    periods.map((period, periodIndex) => {
      const items = entityLevelData.level1Items.map((item) => {
        const showPercentage = Boolean(item.showPercentage);
        const columns = showPercentage
          ? [
            { type: 'percent', key: `${periodIndex}-${item.id}-percent`, label: '%' },
            { type: 'value', key: `${periodIndex}-${item.id}-value`, label: t("Value.label") }
          ]
          : [
            { type: 'value', key: `${periodIndex}-${item.id}-value`, label: t("Value.label") }
          ];
        return { item, showPercentage, columns };
      });

      const colSpan = items.reduce((sum, entry) => sum + entry.columns.length, 0);
      return { period, periodIndex, items, colSpan };
    })
  ), [periods, entityLevelData.level1Items, t]);

  const consPerfValueColumns = useMemo(() => (
    consPerfPeriodGroups.flatMap((group) => group.items.map(({ item }) => ({
      key: `${group.periodIndex}-${item.id}-value`,
      period: group.period,
      periodIndex: group.periodIndex,
      item
    })))
  ), [consPerfPeriodGroups]);

  const consPerfTableRef = useRef(null);

  const syncConsPerfStickyHeaderOffsets = useCallback(() => {
    const table = consPerfTableRef.current;
    if (!table) return;

    const theadRows = table.querySelectorAll('thead tr');
    if (theadRows.length < 2) return;

    const row1Height = Math.ceil(theadRows[0].getBoundingClientRect().height);
    const row2Height = Math.ceil(theadRows[1].getBoundingClientRect().height);

    table.style.setProperty('--esg-cons-perf-thead-row-1-height', `${row1Height}px`);
    table.style.setProperty('--esg-cons-perf-thead-row-2-height', `${row2Height}px`);
  }, []);

  useLayoutEffect(() => {
    if (!hasEntityData) return undefined;

    syncConsPerfStickyHeaderOffsets();

    const table = consPerfTableRef.current;
    if (!table) return undefined;

    const thead = table.querySelector('thead');
    if (!thead) return undefined;

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        syncConsPerfStickyHeaderOffsets();
      });
      resizeObserver.observe(thead);
    }

    window.addEventListener('resize', syncConsPerfStickyHeaderOffsets);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', syncConsPerfStickyHeaderOffsets);
    };
  }, [
    hasEntityData,
    consPerfPeriodGroups,
    sortedEntities,
    sortColumn,
    sortDirection,
    syncConsPerfStickyHeaderOffsets
  ]);

  /** Exclamation only — same visual as stacked bar null marker. Use `noTooltip` beside numeric values (cell click handles drilldown). */
  const renderNullCellIndicator = ({ onOpen, noTooltip } = {}) => {
    const content = (
      <span
        className="esg-consolidated-performance-exclamation-icon d-inline-flex align-items-center justify-content-center"
        aria-hidden={true}
      >
        !
      </span>
    );
    if (noTooltip) {
      return <span className="d-inline-flex align-items-center" onClick={(e) => {
        e.stopPropagation();
        onOpen(e);
      }}>{content}</span>;
    }
    const trigger = onOpen ? (
      <span
        role="button"
        tabIndex={0}
        className="esg-consolidated-performance-null-trigger cursor d-inline-flex align-items-center"
        onClick={(e) => {
          e.stopPropagation();
          onOpen(e);
        }}
        aria-label={nullValueHintText}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            onOpen(e);
          }
        }}
      >
        {content}
      </span>
    ) : (
      <span className="d-inline-flex" aria-label={nullValueHintText}>
        {content}
      </span>
    );
    return (
      <OverlayTrigger
        placement="top"
        overlay={Utils.renderTooltip(nullValueHintText)}
      >
        {trigger}
      </OverlayTrigger>
    );
  };

  const nullOnlyColumns = useMemo(() => {
    if (!hasEntityData || consPerfValueColumns.length === 0) {
      return [];
    }

    return consPerfValueColumns.filter((column) => (
      sortedEntities.some((entity) => {
        const value = entityLevelData.dataByEntityAndPeriod[entity.branchId]?.[column.periodIndex]?.[column.item.id]?.value;
        return isConsolidatedPerformanceExplicitNull(value);
      })
    ));
  }, [hasEntityData, consPerfValueColumns, sortedEntities, entityLevelData.dataByEntityAndPeriod]);

  const nullOnlyEntities = useMemo(() => {
    if (!hasEntityData || nullOnlyColumns.length === 0) return [];
    return sortedEntities.filter((entity) => (
      nullOnlyColumns.some((column) => {
        const value = entityLevelData.dataByEntityAndPeriod[entity.branchId]?.[column.periodIndex]?.[column.item.id]?.value;
        return isConsolidatedPerformanceExplicitNull(value);
      })
    ));
  }, [hasEntityData, nullOnlyColumns, sortedEntities, entityLevelData.dataByEntityAndPeriod]);

  // Open modal with selected row context; render reads directly from configRows + dataViewData.
  const getLevel2Breakdown = (entity, level1Item) => {
    setBreakdownData({
      entity,
      level1Item
    });
    setShowBreakdownModal(true);
  };

  const handleExportExcelSecondCard = () => {
    if (!hasEntityData || !periods?.length || !entityLevelData?.level1Items?.length) return;
    const entityLabel = t('Entity.label') || 'Entity';
    const headerRow1 = [entityLabel];
    consPerfPeriodGroups.forEach((group) => {
      group.items.forEach(({ item, columns }) => {
        const displayLabel = item.label || item.name;
        columns.forEach((column) => {
          const suffix = column.type === 'percent' ? '%' : (t('Value.label') || 'Value');
          headerRow1.push(`${getPeriodLabel(group.period)} - ${displayLabel} ${suffix}`);
        });
      });
    });

    const dataRows = sortedEntities.map((entity) => {
      const row = [entity.branchName || ''];

      consPerfPeriodGroups.forEach((group) => {
        group.items.forEach(({ item, columns }) => {
          const value = entityLevelData.dataByEntityAndPeriod[entity.branchId]?.[group.periodIndex]?.[item.id]?.value;
          const total = totalsByPeriod[group.periodIndex]?.[item.id] || 0;
          const hasNumericValue = value != null && !isNaN(parseFloat(value));

          columns.forEach((column) => {
            if (column.type === 'percent') {
              const percentage = hasNumericValue && total > 0
                ? `${Utils.roundedPercentage((parseFloat(value) / total) * 100)}%`
                : '-';
              row.push(percentage);
            } else {
              row.push(value != null ? Utils.formattedNumber(value) : '-');
            }
          });
        });
      });

      return row;
    });

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet([headerRow1, ...dataRows]);
    const sheetName = t('ESGAnalytics.byOrganizationUnit.label') || 'Consolidated Performance';
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName.substring(0, 31));
    const fileName = `${t('ESGAnalytics.byOrganizationUnit.label') || 'consolidated-performance'}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const renderConsPerfCard = () => {
    if (!hasEntityData) {
      return (
        <Card className="esg-analytics-card esg-consolidated-performance-card">
          <Card.Header className="esg-consolidated-performance-card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0 d-flex align-items-center gap-2">
              {t("ESGAnalytics.byOrganizationUnit.label")}
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="esg-consolidated-performance-cards-tooltip">
                    {t('ESGAnalytics.byOrganizationUnitInfo.label')}
                  </Tooltip>
                }
              >
                <span className="esg-analytics-card-info-icon" aria-label={t('ESGAnalytics.byOrganizationUnitInfo.label')}>
                  <BiInfoCircle size={16} />
                </span>
              </OverlayTrigger>
            </h5>
            <OverlayTrigger
              placement="bottom"
              overlay={Utils.renderTooltip(t('DownloadAsExcel.label'))}
            >
              <span className="d-inline-block">
                <Button
                  variant="esg-excel-download"
                  size="sm"

                  onClick={handleExportExcelSecondCard}
                  disabled
                  aria-label={t('DownloadAsExcel.label')}
                >
                  <BsFileEarmarkExcel size={20} />
                </Button>
              </span>
            </OverlayTrigger>
          </Card.Header>
          <Card.Body>
            <div className="text-center text-muted py-4">
              {t("ESGAnalytics.selectEntitiesToView.label")}
            </div>
          </Card.Body>
        </Card>
      );
    }

    return (
      <Card className="esg-analytics-card esg-consolidated-performance-card">
        <Card.Header className="esg-consolidated-performance-card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0 d-flex align-items-center gap-2">
            {t("ESGAnalytics.byOrganizationUnit.label")}
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="esg-consolidated-performance-cards-tooltip-2">
                  {t('ESGAnalytics.byOrganizationUnitInfo.label')}
                </Tooltip>
              }
            >
              <span className="esg-analytics-card-info-icon" aria-label={t('ESGAnalytics.byOrganizationUnitInfo.label')}>
                <BiInfoCircle size={16} />
              </span>
            </OverlayTrigger>
          </h5>
          <div className="d-flex align-items-center gap-2">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setShowNullValuesModal(true)}
              disabled={nullOnlyColumns.length === 0}
            >
              {t('ESGAnalytics.viewNullValues.label') || 'View null values'}
            </Button>
            <OverlayTrigger
              placement="bottom"
              overlay={Utils.renderTooltip(t('DownloadAsExcel.label'))}
            >
              <Button
                variant="esg-excel-download"
                size="sm"
                className=""
                onClick={handleExportExcelSecondCard}
                aria-label={t('DownloadAsExcel.label')}
              >
                <BsFileEarmarkExcel />
              </Button>
            </OverlayTrigger>
          </div>
        </Card.Header>
        <Card.Body className="p-0 esg-consolidated-performance-table-body">
          <Table ref={consPerfTableRef} bordered className="organization-unit-table esg-consolidated-performance-table">
            <thead>
              <tr>
                <th rowSpan={3} className="esg-sortable-header entity-header">
                  <span
                    className="d-inline-flex align-items-center cursor-pointer"
                    onClick={() => handleSort('entity')}
                  >
                    {t("Entity.label")}
                    {getSortIcon('entity')}
                  </span>
                </th>
                {consPerfPeriodGroups.map((group) => (
                  <th key={group.period} className="text-center" colSpan={group.colSpan}>
                    {getPeriodLabel(group.period)}
                  </th>
                ))}
              </tr>
              <tr>
                {consPerfPeriodGroups.map((group) =>
                  group.items.map(({ item, columns }) => (
                    <th
                      key={`${group.period}-${String(item.id)}-header`}
                      className="text-center"
                      colSpan={columns.length}
                    >
                      {item.label || item.name}
                    </th>
                  ))
                )}
              </tr>
              <tr>
                {consPerfPeriodGroups.map((group) =>
                  group.items.map(({ columns }) => (
                    columns.map((column) => (
                      <th key={column.key} className="text-center esg-sortable-header">
                        <span
                          className="d-inline-flex align-items-center justify-content-center cursor-pointer"
                          onClick={() => handleSort(column.key)}
                        >
                          {column.label}
                          {getSortIcon(column.key)}
                        </span>
                      </th>
                    ))
                  ))
                )}
              </tr>
            </thead>
            <tbody>
              {sortedEntities.map((entity, entityIndex) => (
                <tr key={`entity-${entity.branchId}-${entityIndex}`}>
                  <td className="fw-bold entity-body">{entity.branchName}</td>
                  {consPerfPeriodGroups.map((group) =>
                    group.items.map(({ item, showPercentage }) => {
                      const cell = entityLevelData.dataByEntityAndPeriod[entity.branchId]?.[group.periodIndex]?.[item.id];
                      const value = cell?.value;
                      const hasNullDpsFlag = cell?.hasNullDps === true;
                      const total = totalsByPeriod[group.periodIndex]?.[item.id] || 0;
                      const isExplicitNull = isConsolidatedPerformanceExplicitNull(value);
                      const hasNumericValue = value != null && !isNaN(parseFloat(value));
                      const percentage = hasNumericValue && total > 0
                        ? `${Utils.roundedPercentage((parseFloat(value) / total) * 100)}%`
                        : '-';
                      const displayValue = value != null ? Utils.formattedNumber(value) : '-';
                      const nullValueCellClass = isExplicitNull ? ' esg-consolidated-performance-cell--null' : '';
                      const showNullDpsHint = hasNullDpsFlag && hasNumericValue && !isExplicitNull;

                      return (
                        <React.Fragment key={`${entity.branchId}-${group.period}-${String(item.id)}`}>
                          {showPercentage && (
                            <td className="text-center">
                              {percentage}
                            </td>
                          )}
                          <td
                            className={`text-center${value != null ? ' cursor' : ''}${nullValueCellClass}`}
                            onClick={() => {
                              if (value != null) {
                                getLevel2Breakdown(entity, item);
                              }
                            }}
                          >
                            {isExplicitNull ? (
                              <span className="text-primary">
                                {renderNullCellIndicator({
                                  onOpen: () => openNullDatapointBreakdownModal(entity, item, group.period)
                                })}
                              </span>
                            ) : (
                              <span className="d-inline-flex align-items-center justify-content-center gap-1 flex-wrap">
                                <span className={value != null ? 'text-primary text-decoration-underline' : ''}>
                                  {displayValue}
                                </span>
                                {showNullDpsHint ? renderNullCellIndicator({
                                  noTooltip: true,
                                  onOpen: () => openNullDatapointBreakdownModal(entity, item, group.period)
                                }) : null}
                              </span>
                            )}
                          </td>
                        </React.Fragment>
                      );
                    })
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    );
  };

  const renderBreakdownModal = () => {
    if (!breakdownData) return null;

    const { entity, level1Item } = breakdownData;
    const columnPeriods = periods;

    const configRow = findConfigRowForLevel1Item(configRows, level1Item);
    const level2Items = (configRow?.level2 || []).map(normalizeOrgLevel2Item);

    const getLevel1ValueForPeriod = (period) => {
      const periodIndex = periods.indexOf(period);
      return entityLevelData.dataByEntityAndPeriod[entity.branchId]?.[periodIndex]?.[level1Item.id]?.value;
    };

    const getUomForLevel1 = (period) => {
      const periodData = getPeriodBucketFromDataView(dataViewData, periodType, period);
      if (!periodData) return '';
      if (level1Item.isEquation && level1Item.equationId) {
        return periodData.equations?.find((eq) => eq.equation === level1Item.equationId)?.uom || '';
      }
      if (level1Item.itemType === 'metric') {
        return periodData.metrics?.find((metric) => metric.metricId == level1Item.id)?.uom || '';
      }
      if (level1Item.itemType === 'datapoint') {
        const datapoint = periodData.datapoints?.find((dp) => dp.dataPointId == level1Item.id);
        return getDatapointDisplayUom(datapoint);
      }
      return '';
    };

    const getEntityValueForLevel2 = (level2Item, period) => {
      const periodData = getPeriodBucketFromDataView(dataViewData, periodType, period);
      if (!periodData || !level2Item) return { value: null, uom: '' };

      if (level2Item.itemType === 'equation' && level2Item.eqExpressionWithId) {
        const eq = periodData.equations?.find((entry) => entry.equation === level2Item.eqExpressionWithId);
        const branchData = eq?.branchConsolidated?.find((b) => b.branchId === entity.branchId);
        return { value: branchData?.value ?? null, uom: eq?.uom || '' };
      }

      if (level2Item.itemType === 'metric') {
        const metric = periodData.metrics?.find((entry) => entry.metricId == level2Item.id);
        const branchData = metric?.branchConsolidated?.find((b) => b.branchId === entity.branchId);
        return { value: branchData?.value ?? null, uom: metric?.uom || '' };
      }

      if (level2Item.itemType === 'datapoint') {
        const datapoint = periodData.datapoints?.find((entry) => entry.dataPointId == level2Item.id);
        const branchData = datapoint?.branchConsolidated?.find((b) => b.branchId === entity.branchId);
        return { value: branchData?.value ?? null, uom: getDatapointDisplayUom(datapoint) };
      }

      return { value: null, uom: '' };
    };

    const modalParentKey = `modal-${entity.branchId}-${String(level1Item.id)}`;
    const parentEntityIds = filter?.parentEntities || [];
    const childEntityIds = filter?.childEntities || [];
    const isParentBranch = parentEntityIds.includes(entity?.branchId);
    const isChildBranch = childEntityIds.includes(entity?.branchId);
    // Reported-value column: consolidated performance monthly drilldown for child branches only.
    const showReportedValueColumn = periodType === 'MONTHLY' && isChildBranch && !isParentBranch;
    const valueColumnCount = columnPeriods.length * (showReportedValueColumn ? 2 : 1);

    const renderPeriodValueCells = (getValueAndUom, cellClassName = '') => (
      columnPeriods.map((period) => {
        const { value, uom } = getValueAndUom(period);
        const displayValue = value != null ? Utils.formattedNumber(value) : '-';
        return (
          <React.Fragment key={period}>
            <td className={`text-end${cellClassName ? ` ${cellClassName}` : ''}`}>
              {displayValue}
              {uom && value != null && <span className="text-muted ms-1">({uom})</span>}
            </td>
            {showReportedValueColumn && <td className="text-end text-muted">-</td>}
          </React.Fragment>
        );
      })
    );

    return (
      <Modal
        show={showBreakdownModal}
        onHide={() => setShowBreakdownModal(false)}
        size="fullscreen"
        centered
        className="esg-cons-perf-breakdown-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {entity.branchName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3">
          <div className="esg-cons-perf-breakdown-modal-table-wrap">
            <Table bordered className="esg-analytics-card-table esg-cons-perf-breakdown-modal-table">
              <thead>
                <tr>
                  <th className="esg-cons-perf-breakdown-modal-name-col" aria-hidden="true" />
                  {columnPeriods.map((period) => (
                    <th
                      key={period}
                      className="text-end esg-cons-perf-breakdown-modal-period-col"
                      colSpan={showReportedValueColumn ? 2 : 1}
                    >
                      {getPeriodLabel(period)}
                    </th>
                  ))}
                </tr>
                {showReportedValueColumn && (
                  <tr>
                    <th className="esg-cons-perf-breakdown-modal-name-col" aria-hidden="true" />
                    {columnPeriods.map((period) => (
                      <React.Fragment key={`${period}-subheaders`}>
                        <th className="text-end esg-cons-perf-breakdown-modal-period-col">{t('DataViewNew.inUOM.label')}</th>
                        <th className="text-end esg-cons-perf-breakdown-modal-period-col">{t('DataViewNew.userInput.label')}</th>
                      </React.Fragment>
                    ))}
                  </tr>
                )}
              </thead>
              <tbody>
                <tr className="parent-row">
                  <td className="fw-bold esg-cons-perf-breakdown-modal-name-col">{level1Item.label || level1Item.name}</td>
                  {renderPeriodValueCells((period) => ({
                    value: getLevel1ValueForPeriod(period),
                    uom: getUomForLevel1(period)
                  }), 'fw-bold')}
                </tr>

                {level2Items.length === 0 ? (
                  <tr>
                    <td colSpan={valueColumnCount + 1} className="text-center text-muted ps-4">
                      {t("noDataAvailable.label")}
                    </td>
                  </tr>
                ) : (
                  level2Items.map((item, index) => {
                    const rowKey = `${modalParentKey}-${item.id || item.eqExpressionWithId || item.code || item.name}`;
                    const isExpanded = expandedLevel2Rows[rowKey] === true;
                    const isDrillable = isDrillableLevel2Row(item);
                    const itemName = getOrgBreakdownLevel2ItemName(item, configRow || { level2: [] });

                    return (
                      <React.Fragment key={`level2-${index}`}>
                        <tr
                          className={`child-row ${isDrillable ? 'cursor' : ''}`}
                          onClick={() => {
                            if (isDrillable) {
                              handleLevel2RowToggle(modalParentKey, item, entity.branchId);
                            }
                          }}
                        >
                          <td className="ps-4 esg-cons-perf-breakdown-modal-name-col">
                            <span className="text-muted">└─</span>
                            {isDrillable ? (
                              <span className="ms-1 me-1 d-inline-flex align-items-center">
                                {isExpanded ? <BiChevronDown size={16} /> : <BiChevronRight size={16} />}
                              </span>
                            ) : (
                              <span className="ms-1 me-1 d-inline-flex align-items-center" style={{ width: '16px' }} />
                            )}
                            {itemName}
                          </td>
                          {renderPeriodValueCells((period) => getEntityValueForLevel2(item, period))}
                        </tr>
                        {isExpanded && isDrillable && renderDrilldownRows(rowKey, item, columnPeriods, showReportedValueColumn)}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBreakdownModal(false)}>
            {t("Close.label")}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  const renderNullValuesModal = () => {
    const periodTypeForNullModal = filter?.periodType || 'YEARLY';

    const sortSubPeriodLabels = (subLabels) => {
      if (periodTypeForNullModal === 'MONTHLY') {
        return [...subLabels].sort((a, b) => {
          const ia = monthOrder.indexOf(a);
          const ib = monthOrder.indexOf(b);
          if (ia === -1 && ib === -1) return String(a).localeCompare(String(b));
          if (ia === -1) return 1;
          if (ib === -1) return -1;
          return ia - ib;
        });
      }
      if (periodTypeForNullModal === 'QUARTERLY') {
        return [...subLabels].sort((a, b) => {
          const ia = quarterOrder.indexOf(a);
          const ib = quarterOrder.indexOf(b);
          if (ia === -1 && ib === -1) return String(a).localeCompare(String(b));
          if (ia === -1) return 1;
          if (ib === -1) return -1;
          return ia - ib;
        });
      }
      return [...subLabels].sort((a, b) => String(a).localeCompare(String(b)));
    };

    const sortMissingEntries = (entries) =>
      [...entries].sort((a, b) => {
        const cmp = a.sortLabel.localeCompare(b.sortLabel);
        if (cmp !== 0) return cmp;
        return String(a.key).localeCompare(String(b.key));
      });

    const entityMissingBlocks = (() => {
      if (!hasEntityData || nullOnlyColumns.length === 0 || nullOnlyEntities.length === 0) return [];
      return nullOnlyEntities.map((entity) => {
        const missingByYear = {};
        const missingByYearSub = {};

        nullOnlyColumns.forEach((column) => {
          const value = entityLevelData.dataByEntityAndPeriod[entity.branchId]?.[column.periodIndex]?.[column.item.id]?.value;
          if (!isConsolidatedPerformanceExplicitNull(value)) return;

          const periodStr = String(column.period);
          const periodParts = periodStr.split('-');
          const yearLabel = periodParts[0];
          const lines = getNullModalDisplayLines(configRows, column.item);
          const pushEntries = (bucket) => {
            lines.forEach((line, lineIdx) => {
              bucket.push({
                key: `${entity.branchId}-${column.key}-L${lineIdx}`,
                text: line.text,
                sortLabel: line.sortLabel
              });
            });
          };

          if (periodTypeForNullModal === 'YEARLY') {
            if (!missingByYear[yearLabel]) missingByYear[yearLabel] = [];
            pushEntries(missingByYear[yearLabel]);
          } else {
            const subLabel = periodParts.length > 1 ? periodParts.slice(1).join('-') : '';
            if (!missingByYearSub[yearLabel]) missingByYearSub[yearLabel] = {};
            if (!missingByYearSub[yearLabel][subLabel]) missingByYearSub[yearLabel][subLabel] = [];
            pushEntries(missingByYearSub[yearLabel][subLabel]);
          }
        });

        const years = [...new Set([
          ...Object.keys(missingByYear),
          ...Object.keys(missingByYearSub)
        ])].sort((a, b) => {
          const na = Number(a);
          const nb = Number(b);
          if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
          return String(a).localeCompare(String(b));
        });

        const groupedMissing = years.map((year) => {
          if (periodTypeForNullModal === 'YEARLY') {
            const entries = sortMissingEntries(missingByYear[year] || []);
            return { year, isYearly: true, entries, subPeriods: null };
          }
          const subMap = missingByYearSub[year] || {};
          const subLabels = sortSubPeriodLabels(Object.keys(subMap));
          const subPeriods = subLabels.map((subLabel) => ({
            subLabel,
            entries: sortMissingEntries(subMap[subLabel] || [])
          }));
          return { year, isYearly: false, entries: null, subPeriods };
        }).filter((block) => {
          if (block.isYearly) return (block.entries || []).length > 0;
          return (block.subPeriods || []).some((s) => (s.entries || []).length > 0);
        });

        return { entity, groupedMissing };
      }).filter((row) => row.groupedMissing.length > 0);
    })();

    return (
      <Modal
        show={showNullValuesModal}
        onHide={() => setShowNullValuesModal(false)}
        size="lg"
        centered
        className="esg-null-values-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t('ESGAnalytics.nullValuesModalTitle.label') || 'Missing reported values by organisation unit'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="esg-null-values-modal-body">
          {entityMissingBlocks.length === 0 ? (
            <div className="text-center text-muted py-4">
              {t('ESGAnalytics.noNullValuesFound.label') || 'No null values found'}
            </div>
          ) : (
            <>
              <p className="esg-null-values-modal-intro text-muted mb-3">
                {t('ESGAnalytics.nullValuesModalIntro.label')}
              </p>
              <div className="esg-null-values-entity-list">
                {entityMissingBlocks.map(({ entity, groupedMissing }) => (
                  <section
                    key={`null-gap-entity-${entity.branchId}`}
                    className="esg-null-values-entity-block"
                    data-testid="esg-null-values-entity"
                  >
                    <h6 className="esg-null-values-entity-title">{entity.branchName}</h6>
                    <ul className="esg-null-values-year-list list-unstyled mb-0">
                      {groupedMissing.map((yearBlock) => (
                        <li key={`${entity.branchId}-${yearBlock.year}`} className="esg-null-values-year-item">
                          <div className="esg-null-values-year-label">{yearBlock.year}</div>
                          {yearBlock.isYearly ? (
                            <ul className="esg-null-values-missing-list list-unstyled mb-0">
                              {yearBlock.entries.map((entry) => (
                                <li
                                  key={entry.key}
                                  className="esg-null-values-missing-item"
                                  data-testid="esg-null-values-missing-item"
                                >
                                  {entry.text}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <ul className="esg-null-values-subperiod-list list-unstyled mb-0">
                              {(yearBlock.subPeriods || []).map((sp) => (
                                <li key={`${entity.branchId}-${yearBlock.year}-${sp.subLabel || 'none'}`} className="esg-null-values-subperiod-item">
                                  <div className="esg-null-values-subperiod-label">{sp.subLabel}</div>
                                  <ul className="esg-null-values-missing-list list-unstyled mb-0">
                                    {sp.entries.map((entry) => (
                                      <li
                                        key={entry.key}
                                        className="esg-null-values-missing-item"
                                        data-testid="esg-null-values-missing-item"
                                      >
                                        {entry.text}
                                      </li>
                                    ))}
                                  </ul>
                                </li>
                              ))}
                            </ul>
                          )}
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNullValuesModal(false)}>
            {t("Close.label")}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  /** Consolidated bucket for a period (metrics / datapoints / equations), same shape as API example. */
  const getPeriodDataFromDataView = (period) => {
    return getPeriodBucketFromDataView(dataViewData, periodType, period);
  };

  const normalizeOrgLevel2Item = (item) => ({
    id: item.id ?? item.metricId ?? item.dataPointId,
    itemType: item.itemType,
    name: item.name || item.metricName || item.dataPointName || '',
    code: item.code || item.metricCode || item.dataPointCode || '',
    eqExpressionWithId: item.eqExpressionWithId || null,
    eqExpression: item.eqExpression || null,
    label: item.label || '',
    drillDownEnabled: item.drillDownEnabled === true,
    drillDownType: item.drillDownType || 'equation'
  });

  const getOrgBreakdownLevel2FallbackUom = (level2Item) => {
    if (level2Item.itemType !== 'metric' && level2Item.itemType !== 'datapoint') {
      return '';
    }
    const resolvedId = level2Item.id ?? level2Item.metricId ?? level2Item.dataPointId;
    for (const p of periods) {
      const pd = getPeriodDataFromDataView(p);
      if (!pd) continue;
      if (level2Item.itemType === 'metric' && resolvedId != null) {
        const m = pd.metrics?.find((x) => x.metricId == resolvedId);
        if (m?.uom) return m.uom;
      }
      if (level2Item.itemType === 'datapoint' && resolvedId != null) {
        const dp = pd.datapoints?.find((x) => x.dataPointId == resolvedId);
        if (dp) return getDatapointDisplayUom(dp);
      }
    }
    return '';
  };

  /** Level-1 values: only `configRow.level1`. `itemType`: `metric` | `equation` | `datapoint`. For `metric`/`equation`, equation strings live on `eqExpression` / `eqExpressionWithId`; API `equations[].equation` matches `eqExpressionWithId`. */
  const getOrgBreakdownLevel1Display = (configRow, period) => {
    const periodData = getPeriodDataFromDataView(period);
    const l1 = configRow?.level1;
    if (!periodData || !l1) {
      return { value: '-', uom: '' };
    }

    const itemType = l1.itemType;

    if (itemType === 'equation') {
      const eqId = l1.eqExpressionWithId || null;
      if (!eqId || !periodData.equations) {
        return { value: '-', uom: '' };
      }
      const matched = periodData.equations.find((eq) => eq.equation === eqId);
      if (matched && matched.total != null) {
        return { value: Utils.formattedNumber(matched.total), uom: matched.uom || '' };
      }
      return { value: '-', uom: '' };
    }

    if (itemType === 'metric') {
      if (l1.id == null) return { value: '-', uom: '' };
      const metric = periodData.metrics?.find((m) => m.metricId === l1.id);
      if (metric && metric.total != null) {
        return { value: Utils.formattedNumber(metric.total), uom: metric.uom || '' };
      }
      return { value: '-', uom: '' };
    }

    if (itemType === 'datapoint') {
      if (l1.id == null) return { value: '-', uom: '' };
      const dp = periodData.datapoints?.find((d) => d.dataPointId === l1.id);
      if (dp && dp.total != null) {
        return { value: Utils.formattedNumber(dp.total), uom: getDatapointDisplayUom(dp) };
      }
      return { value: '-', uom: '' };
    }

    return { value: '-', uom: '' };
  };

  const getOrgBreakdownLevel2ValueForPeriod = (level2Item, period) => {
    const periodData = getPeriodDataFromDataView(period);
    if (!periodData) {
      return { value: '-', uom: '' };
    }
    const resolvedId = level2Item.id ?? level2Item.metricId ?? level2Item.dataPointId;

    if (level2Item.itemType === 'metric') {
      if (resolvedId == null) return { value: '-', uom: '' };
      const found = periodData.metrics?.find((m) => m.metricId == resolvedId);
      if (found && found.total != null) {
        return {
          value: Utils.formattedNumber(found.total),
          uom: found.uom || getOrgBreakdownLevel2FallbackUom(level2Item) || ''
        };
      }
      return { value: '-', uom: '' };
    }

    if (level2Item.itemType === 'datapoint') {
      if (resolvedId == null) return { value: '-', uom: '' };
      const found = periodData.datapoints?.find((dp) => dp.dataPointId == resolvedId);
      if (found && found.total != null) {
        return {
          value: Utils.formattedNumber(found.total),
          uom: getDatapointDisplayUom(found, getOrgBreakdownLevel2FallbackUom(level2Item))
        };
      }
      return { value: '-', uom: '' };
    }

    if (level2Item.itemType === 'equation' && level2Item.eqExpressionWithId && periodData.equations) {
      const equation = periodData.equations.find((eq) => eq.equation === level2Item.eqExpressionWithId);
      if (equation && equation.total != null) {
        return {
          value: Utils.formattedNumber(equation.total),
          uom: equation.uom || ''
        };
      }
    }
    return { value: '-', uom: '' };
  };

  const getOrgBreakdownLevel2ItemName = (level2Item, configRow) => {
    if (level2Item.itemType === 'metric' || level2Item.itemType === 'datapoint') {
      const resolvedId = level2Item.id ?? level2Item.metricId ?? level2Item.dataPointId;
      const match = configRow.level2?.find(
        (rc) => (rc.id ?? rc.metricId ?? rc.dataPointId) == resolvedId
      );
      return match?.label ?? level2Item.name ?? '';
    }
    if (level2Item.eqExpressionWithId) {
      const match = configRow.level2?.find((rc) => rc.eqExpressionWithId === level2Item.eqExpressionWithId);
      return match?.label || level2Item.name || '';
    }
    return level2Item.name || '';
  };

  const formatOrgBreakdownExcelCell = (valueStr, uomStr) => {
    if (valueStr == null || valueStr === '' || valueStr === '-') return '-';
    return uomStr ? `${valueStr} (${uomStr})` : String(valueStr);
  };

  const formatDrillCellForOrgExport = (drillRow, periodKey) => {
    const raw = drillRow.valuesByPeriod?.[periodKey];
    const value = raw != null ? Utils.formattedNumber(raw) : '-';
    const uom = drillRow.uomByPeriod?.[periodKey] || '';
    return formatOrgBreakdownExcelCell(value, uom);
  };

  function appendRecursiveDrillRowsForExport(parentKey, drillRows, depth, columnPeriods, dataRows) {
    (drillRows || []).forEach((drillRow) => {
      const currentRowKey = `${parentKey}-${drillRow.key || drillRow.code || drillRow.name}`;
      const indent = `${'  '.repeat(Math.max(1, depth + 1))}↳ `;
      dataRows.push([
        `${indent}${drillRow.name || ''}`,
        ...columnPeriods.map((p) => formatDrillCellForOrgExport(drillRow, p))
      ]);

      const canDrill = isNestedDrillableRow(drillRow);
      const isExpanded = expandedNestedRows[currentRowKey] === true;
      if (!isExpanded || !canDrill) {
        return;
      }

      const currentState = nestedDrilldownData[currentRowKey];
      if (currentState?.loading) {
        dataRows.push([
          `${'  '.repeat(depth + 2)}↳ ${t('Loading.label') || 'Loading...'}`,
          ...columnPeriods.map(() => '-')
        ]);
        return;
      }
      if (currentState?.error) {
        dataRows.push([
          `${'  '.repeat(depth + 2)}↳ ${currentState.error}`,
          ...columnPeriods.map(() => '-')
        ]);
        return;
      }
      if (!currentState?.rows?.length) {
        dataRows.push([
          `${'  '.repeat(depth + 2)}↳ ${t('ESGAnalytics.noDataAvailable.label')}`,
          ...columnPeriods.map(() => '-')
        ]);
        return;
      }
      appendRecursiveDrillRowsForExport(currentRowKey, currentState.rows, depth + 1, columnPeriods, dataRows);
    });
  }

  function appendExpandedDrilldownRowsForExport(parentRowKey, columnPeriods, dataRows) {
    const drillState = level2DrilldownData[parentRowKey];
    if (!drillState) {
      return;
    }
    if (drillState.loading) {
      dataRows.push([
        `    ↳ ${t('Loading.label') || 'Loading...'}`,
        ...columnPeriods.map(() => '-')
      ]);
      return;
    }
    if (drillState.error) {
      dataRows.push([
        `    ↳ ${drillState.error}`,
        ...columnPeriods.map(() => '-')
      ]);
      return;
    }
    const drillRows = drillState.rows || [];
    if (drillRows.length === 0) {
      dataRows.push([
        `    ↳ ${t('ESGAnalytics.noDataAvailable.label')}`,
        ...columnPeriods.map(() => '-')
      ]);
      return;
    }
    appendRecursiveDrillRowsForExport(parentRowKey, drillRows, 1, columnPeriods, dataRows);
  }

  const handleExportOrgBreakdownExcel = () => {
    if (!configRows?.length || !periods?.length) {
      return;
    }

    const nameColumn = t('Name.label') || 'Name';
    const headerRow = [nameColumn, ...periods.map((p) => getPeriodLabel(p))];
    const dataRows = [];

    configRows.forEach((configRow, index) => {
      dataRows.push([
        configRow.name || '',
        ...periods.map((period) => {
          const { value, uom } = getOrgBreakdownLevel1Display(configRow, period);
          return formatOrgBreakdownExcelCell(value, uom);
        })
      ]);

      const level2Items = (configRow.level2 || []).map(normalizeOrgLevel2Item);
      level2Items.forEach((level2Item) => {
        const itemName = getOrgBreakdownLevel2ItemName(level2Item, configRow);
        const rowKey = `${index}-${level2Item.id || level2Item.eqExpressionWithId || level2Item.code || level2Item.name}`;
        dataRows.push([
          `└─ ${itemName}`,
          ...periods.map((period) => {
            const { value, uom } = getOrgBreakdownLevel2ValueForPeriod(level2Item, period);
            return formatOrgBreakdownExcelCell(value, uom);
          })
        ]);

        const isExpanded = expandedLevel2Rows[rowKey] === true;
        const isDrillable = isDrillableLevel2Row(level2Item);
        if (isExpanded && isDrillable) {
          appendExpandedDrilldownRowsForExport(rowKey, periods, dataRows);
        }
      });
    });

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);
    const sheetTitle = (t('ESGAnalytics.comparison.label') || 'Organisational Breakdown').substring(0, 31);
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetTitle);
    const rawStem = t('ESGAnalytics.comparison.label') || 'organisational-breakdown';
    const safeStem = String(rawStem).replace(/[\\/:*?"<>|]/g, '_');
    const fileName = `${safeStem}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const renderOrgBreakdownCardHeader = ({ disableExport = false } = {}) => (
    <Card.Header className="esg-org-breakdown-card-header d-flex justify-content-between align-items-center">
      <h5 className="mb-0 d-flex align-items-center gap-2">
        {t("ESGAnalytics.comparison.label")}
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id="esg-first-card-comparison-info-tooltip-2">
              {t('ESGAnalytics.comparisonInfo.label')}
            </Tooltip>
          }
        >
          <span className="esg-analytics-card-info-icon" aria-label={t('ESGAnalytics.comparisonInfo.label')}>
            <BiInfoCircle size={16} />
          </span>
        </OverlayTrigger>
      </h5>
      <OverlayTrigger
        placement="bottom"
        overlay={Utils.renderTooltip(t('DownloadAsExcel.label'))}
      >
        <span className="d-inline-block">
          <Button
            variant="esg-excel-download"
            size="sm"
            onClick={handleExportOrgBreakdownExcel}
            disabled={disableExport}
            aria-label={t('DownloadAsExcel.label')}
          >
            <BsFileEarmarkExcel size={20} />
          </Button>
        </span>
      </OverlayTrigger>
    </Card.Header>
  );

  const renderEmptyState = () => (
    <Card className="esg-analytics-card esg-org-breakdown-card">
      {renderOrgBreakdownCardHeader({ disableExport: true })}
      <Card.Body>
        <div className="text-center text-muted py-4">
          {t("ESGAnalytics.noDataAvailable.label")}
        </div>
      </Card.Body>
    </Card>
  );

  const renderOrgBreakdownParentRow = (configRow, index) => (
    <tr key={`parent-${index}`} className="parent-row">
      <td className="fw-bold">{configRow.name}</td>
      {periods.map((period) => {
        const { value, uom } = getOrgBreakdownLevel1Display(configRow, period);
        return (
          <td key={period} className="text-end fw-bold">
            {value} {uom && value !== '-' && <span className="text-muted">({uom})</span>}
          </td>
        );
      })}
    </tr>
  );

  const renderOrgBreakdownLevel2Row = (level2Item, configRow, index, itemIndex) => {
    const itemName = getOrgBreakdownLevel2ItemName(level2Item, configRow);
    const rowKey = `${index}-${level2Item.id || level2Item.eqExpressionWithId || level2Item.code || level2Item.name}`;
    const isExpanded = expandedLevel2Rows[rowKey] === true;
    const isDrillable = isDrillableLevel2Row(level2Item);
    return (
      <React.Fragment key={`level2-${index}-${itemIndex}`}>
        <tr
          className={`child-row ${isDrillable ? 'cursor' : ''}`}
          onClick={() => {
            if (isDrillable) {
              handleLevel2RowToggle(index, level2Item);
            }
          }}
        >
          <td className="ps-4">
            <span className="text-muted">└─</span>
            {isDrillable ? (
              <span className="ms-1 me-1 d-inline-flex align-items-center">
                {isExpanded ? <BiChevronDown size={16} /> : <BiChevronRight size={16} />}
              </span>
            ) : (
              <span className="ms-1 me-1 d-inline-flex align-items-center" style={{ width: '16px' }} />
            )}
            {itemName}
            <EquationComponentsPopover
              standardId={filter?.standardId}
              equationId={level2Item.eqExpressionWithId || null}
              headerText={level2Item.eqExpression || level2Item.eqExpressionWithId || level2Item.code || itemName}
              popoverKey={`org-${index}-${itemIndex}-${level2Item.id || level2Item.eqExpressionWithId || level2Item.code || level2Item.name}`}
              className="esg-eq-info-trigger ms-2"
              popoverClassName={equationPopoverClassName}
              iconSize={14}
              stopPropagation={true}
              showWhenNoEquationId={level2Item?.itemType === 'metric'}
            />
          </td>
          {periods.map((period) => {
            const { value, uom } = getOrgBreakdownLevel2ValueForPeriod(level2Item, period);
            return (
              <td key={period} className="text-end">
                {value} {uom && value !== '-' && <span className="text-muted">({uom})</span>}
              </td>
            );
          })}
        </tr>
        {isExpanded && isDrillable && renderDrilldownRows(rowKey, level2Item)}
      </React.Fragment>
    );
  };

  const renderOrgBreakDownCard = () => {
    if (!configRows || configRows.length === 0 || periods.length === 0) {
      return renderEmptyState();
    }

    return (
      <Card className="esg-analytics-card esg-org-breakdown-card">
        {renderOrgBreakdownCardHeader({ disableExport: false })}
        <Card.Body className="p-0 esg-org-breakdown-table-body">
          <Table bordered className="esg-analytics-card-table esg-org-breakdown-table">
            <thead>
              <tr>
                <th className="Organisational-header"></th>
                {periods.map(period => (
                  <th key={period} className="text-end">{getPeriodLabel(period)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {configRows.map((configRow, index) => {
                const level2Items = (configRow.level2 || []).map(normalizeOrgLevel2Item);
                return (
                  <React.Fragment key={`config-row-${configRow.id ?? index}`}>
                    {renderOrgBreakdownParentRow(configRow, index)}
                    {level2Items.map((level2Item, itemIndex) =>
                      renderOrgBreakdownLevel2Row(level2Item, configRow, index, itemIndex)
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    );
  };

  const openNullDatapointBreakdownModal = (entity, level1Item, period) => {
    if (level1Item.itemType === 'datapoint') return;

    setNullDpModalContext({
      title: `${entity.branchName} - ${getPeriodLabel(period)}`,
      period,
      periodType,
      branchId: entity.branchId,
      requestPayload: buildNullDatapointBreakdownPayload(configRows, level1Item, entity.branchId, period, filter)
    });
  };

  const isEmissionDatapoint = (datapoint) =>
    datapoint?.emission === true || datapoint?.emissionLinked === true;

  const getDatapointDisplayUom = (datapoint, fallback = '') =>
    isEmissionDatapoint(datapoint) ? EMISSION_DISPLAY_UOM : (datapoint?.uom || fallback || '');

  // section: 'first' | 'second' | 'all' - which card(s) to render (for custom page order)
  if (section === 'second') {
    return (
      <div className="esg-analytics-cards-container mb-3 1" data-testid="esg-analytics-cards">
        <div className="row g-3">
          <div className="col-md-12 p-0">
            {renderConsPerfCard()}
          </div>
        </div>
        {renderBreakdownModal()}
        {nullDpModalContext && (
          <ESGAnalyticsDPBreakdownModal
            context={nullDpModalContext}
            onHide={() => setNullDpModalContext(null)}
          />
        )}
        {renderNullValuesModal()}
      </div>
    );
  }
  if (section === 'first') {
    return (
      <div className="esg-analytics-cards-container mb-3 2" data-testid="esg-analytics-cards">
        <div className="row g-3">
          <div className="col-md-12 p-0">
            {renderOrgBreakDownCard()}
          </div>
        </div>
        {renderBreakdownModal()}
      </div>
    );
  }
  return (
    <div className="esg-analytics-cards-container mb-3 3" data-testid="esg-analytics-cards">
      <div className="row g-3 border p-3">
        <div className="col-md-12 p-0">
          {renderConsPerfCard()}
        </div>
        <div className="col-md-12 p-0">
          {renderOrgBreakDownCard()}
        </div>
      </div>
      {renderBreakdownModal()}
      {nullDpModalContext && (
        <ESGAnalyticsDPBreakdownModal
          context={nullDpModalContext}
          onHide={() => setNullDpModalContext(null)}
        />
      )}
      {renderNullValuesModal()}
    </div>
  );
};

export default withTranslation()(ESGAnalyticsCards);

